import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavbarComponent } from "../../shared/components/navbar/navbar.component";
import { FooterComponent } from "../../shared/components/footer/footer.component";
import { AuthService } from '@auth0/auth0-angular';
import { ApiService, Subscription, SubscriptionBenefit } from 'src/app/core/services/api.service';
import { tap } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatTableModule } from '@angular/material/table';
import { Credit } from 'src/app/core/models/credit';
import { NavigationRailComponent } from '../../shared/components/navigation-rail/navigation-rail.component';
import { TutorialService } from 'src/app/core/tutorial/tutorial.service';
import { Router } from '@angular/router';
import { VesselSearchCandidate } from 'src/app/core/services/api.service';
import { SubscriptionEntitlementService } from 'src/app/core/services/subscription-entitlement.service';

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NavbarComponent,
    FooterComponent,
    MatProgressSpinnerModule,
    MatCardModule,
    MatDividerModule,
    MatTableModule,
    NavigationRailComponent
  ]
})
export class AccountComponent implements OnInit {
  auth = inject(AuthService);
  apiService = inject(ApiService);
  tutorial = inject(TutorialService);
  router = inject(Router);
  subscriptionEntitlement = inject(SubscriptionEntitlementService);
  
  credits: Credit[] = [];
  benefits: SubscriptionBenefit[] = [];
  subscription: Subscription | null = null;
  cancellingSubscription = false;
  cancellationError = '';
  creditHistoryColumns: string[] = ['Amount', 'Remaining', 'Source', 'Type', 'ValidDates'];
  loadingCredits = false;
  loadingBenefits = false;

  ngOnInit(): void {
    this.loadingCredits = true;

    this.auth.user$.pipe().subscribe(() => {
      this.loadUserCredits();
      this.loadSubscriptionBenefits();
    });
  }

  logout(): void {
    this.auth.logout({
      logoutParams: { returnTo: window.location.origin }
    });
  }

  replayQuickTour(): void {
    void this.tutorial.start('replay');
  }

  openVesselFromSearch(candidate: VesselSearchCandidate): void {
    const queryParams: Record<string, string> = {};
    if (candidate.imo) queryParams['vesselImo'] = candidate.imo;
    if (candidate.name) queryParams['vesselName'] = candidate.name;
    void this.router.navigate(['/'], { queryParams, state: { vesselCandidate: candidate } });
  }

  loadUserCredits() {
    this.loadingCredits = true;
    this.apiService.listCredits().subscribe({
      next: (credits) => {
        this.credits = credits;
        this.loadingCredits = false;
      },
      error: (error) => {
        console.error('Error loading credits:', error);
        this.loadingCredits = false;
      }
    });
  }

  loadSubscriptionBenefits(): void {
    this.loadingBenefits = true;
    this.apiService.getMySubscription().subscribe({
      next: ({ subscription }) => {
        this.subscription = subscription;
        this.benefits = subscription?.benefits || [];
        this.loadingBenefits = false;
      },
      error: () => {
        this.subscription = null;
        this.benefits = [];
        this.loadingBenefits = false;
      }
    });
  }

  cancelSubscription(): void {
    if (!this.subscription?.id || this.cancellingSubscription || this.subscription.status !== 'active') return;
    if (!window.confirm('Cancel your subscription?')) return;
    this.cancellingSubscription = true;
    this.cancellationError = '';
    this.apiService.cancelSubscription(this.subscription.id).subscribe({
      next: ({ subscription }) => {
        this.subscription = subscription;
        this.benefits = subscription?.benefits || [];
        this.subscriptionEntitlement.refresh();
        this.cancellingSubscription = false;
      },
      error: () => {
        this.cancellationError = 'The subscription could not be canceled. Please try again.';
        this.cancellingSubscription = false;
      }
    });
  }

  getSubscriptionDescription(): string {
    switch (this.subscription?.status) {
      case 'active': return 'Active subscription';
      case 'canceling': return 'Cancellation scheduled';
      case 'canceled': return 'Subscription canceled';
      default: return 'Subscription status unavailable';
    }
  }

  getSubscriptionStatusLabel(): string {
    switch (this.subscription?.status) {
      case 'active': return 'ACTIVE';
      case 'canceling': return 'CANCELING';
      case 'canceled': return 'CANCELED';
      default: return (this.subscription?.status || 'UNKNOWN').toUpperCase();
    }
  }

  getBenefitLabel(benefit: SubscriptionBenefit): string {
    return benefit.code === 'ucb' ? 'Vessel UCB reports' : 'Standard AVI report';
  }

  getBenefitDescription(benefit: SubscriptionBenefit): string {
    return benefit.code === 'ucb'
      ? 'Ultimate control and beneficiary investigation'
      : 'Advanced vessel intelligence investigation';
  }

  getCreditSourceLabel(source: string | undefined): string {
    if (!source) return 'N/A';
    
    if (source === 'payment') return 'Payment';
    if (source === 'admin') return 'Admin';
    if (source === 'promotion') return 'Free Trial';
    
    return source;
  }

  getEntityTypeLabel(type: string | undefined): string {
    if (type === 'user') return 'Personal';
    if (type === 'organization') return 'Organization';
    return 'N/A';
  }

  getUsedAmount(credit: Credit): number {
    return credit.amount - credit.remaining_amount;
  }

  getUsagePercentage(credit: Credit): number {
    return (this.getUsedAmount(credit) / credit.amount) * 100;
  }

  getTotalCreditAmount(): number {
    return this.credits.reduce((total, credit) => total + credit.amount, 0);
  }

  getTotalRemainingAmount(): number {
    return this.credits.reduce((total, credit) => total + credit.remaining_amount, 0);
  }

  getTotalUsedAmount(): number {
    return this.getTotalCreditAmount() - this.getTotalRemainingAmount();
  }

  getTotalUsagePercentage(): number {
    const total = this.getTotalCreditAmount();
    if (total === 0) return 0;
    return (this.getTotalUsedAmount() / total) * 100;
  }

  getValidityPeriod(credit: Credit): string {
    const startDate = credit.startDate ? new Date(credit.startDate).toLocaleDateString() : 'N/A';
    const endDate = credit.endDate ? new Date(credit.endDate).toLocaleDateString() : 'No expiration';
    return `${startDate} - ${endDate}`;
  }
}
